import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";

// Utils
import { sanitizeFileName } from "../../../../utils";
import areObjectsEqual from '../../../../utils/areObjectsEqual';
import { maxFileSizeValidation } from "../../../../utils/common";
import constants from "../../../../utils/constants";

// Components
import Dropdown from "../../../Common/Dropdown";

// Context
import { useGlobal } from "../../../../context/GlobalContextProvider";
import { useWallet } from "../../../../context/WalletContextProvider";

// Constants
const ALLOWED_NAME_REGEX = /^[a-zA-Z0-9\s\/_-]*$/;
const INITIAL_ERRORS = {
  artPackName: false,
  artPackNameInvalid: false,
  copyrightNoticeUrl: false,
  checkName: false,
  minArtistPercentage: false,
  minArtistPercentageRange: false,
  artPackCover: false,
};

// API Service
const artPackAPI = {
  checkNameUnique: async (name, id) => {
    const encodedName = encodeURIComponent(name);
    const res = await fetch(`/api/artist/artpack/checkname/${encodedName}/${id}`);
    if (res.status === 200) {
      const data = await res.json();
      return data.checkStatus;
    }
    return false;
  },

  uploadImage: async (file, encryptedName) => {
    const formData = new FormData();
    formData.append("file", file, encryptedName);
    return axios.post(`/api/file/upload`, formData);
  },

  fetchImage: async (name) => {
    if (!name) return null;
    
    const res = await fetch(`/api/file/name/${name}`);
    if (!res.ok) {
      throw new Error(`Failed to fetch image. Status: ${res.status}`);
    }
    
    const data = await res.json();
    if (!data?.image?.data) {
      throw new Error("Invalid image data received.");
    }

    const uint8Array = new Uint8Array(data.image.data);
    const binaryString = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
    return `data:${data.type};base64,${btoa(binaryString)}`;
  },
};

// Validation helpers
const validateArtPackName = (name) => ALLOWED_NAME_REGEX.test(name);

const validatePercentageRange = (value) => {
  return value >= 0 && value <= constants.MAX_MIN_ARTIST_PERCENTAGE;
};


// Form component for managing and submitting an Art Pack form
const Form = ({ formData, handleSubmit, onDiscard, typeCheck }) => {
  const { artPack, setSpinner } = useGlobal();
  const { apiConstants } = useWallet();

  const [form, setForm] = useState(formData);
  const [errors, setErrors] = useState(INITIAL_ERRORS);


  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !areObjectsEqual({ ...formData }, form);
  }, [formData, form]);

  // Load image preview for edit mode
  useEffect(() => {
    const loadImage = async () => {
      if (form?.id && typeCheck === 'updateItem' && form.preview_url && !form.uploadedfile) {
        try {
          const thumbnailUrl = await artPackAPI.fetchImage(form.preview_url);
          setForm(prev => ({ ...prev, preview_thumbnail_url: thumbnailUrl }));
        } catch (error) {
          console.error("Error loading image:", error);
        }
      }
    };
    loadImage();
  }, [form?.id, form?.preview_url, typeCheck]);

  // Reset all errors
  const resetErrors = useCallback(() => {
    setErrors(INITIAL_ERRORS);
  }, []);

  // Check if art pack name is unique
  const checkNameUnique = useCallback(async (name, id) => {
    const isNameTaken = await artPackAPI.checkNameUnique(name, id);
    setErrors(prev => ({ ...prev, checkName: isNameTaken }));
    return isNameTaken;
  }, []);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    // Special validation for art_pack_name
    if (name === 'art_pack_name') {
      if (value && !validateArtPackName(value)) {
        setErrors(prev => ({ ...prev, artPackNameInvalid: true }));
        return;
      }
      setErrors(prev => ({ ...prev, artPackNameInvalid: false }));
    }

    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  // Handle dropdown changes
  const handleDropdownChange = useCallback((name, value) => {
    if (name === "available_for_campaigns") {
      const boolValue = value === "Yes" ? true : value === "No" ? false : value;
      setForm(prev => ({ ...prev, [name]: boolValue }));
    }
  }, []);

  // Handle image selection
  const handleImageChange = useCallback((file) => {
    if (!file) return;

    const error = maxFileSizeValidation(file);
    if (error) {
      setErrors(prev => ({ ...prev, artPackCover: error }));
      return;
    }

    setErrors(prev => ({ ...prev, artPackCover: false }));
    setForm(prev => ({
      ...prev,
      uploadedfile: file,
      preview_url: sanitizeFileName(file.name),
      preview_thumbnail_url: URL.createObjectURL(file),
    }));
  }, []);

  // Handle file input change
  const onImageChange = useCallback((e) => {
    handleImageChange(e.target.files[0]);
  }, [handleImageChange]);

  // Handle drag and drop
  const onDrop = useCallback((acceptedFiles) => {
    handleImageChange(acceptedFiles[0]);
  }, [handleImageChange]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  // Validate form and submit
  const handleClick = useCallback(async () => {
    resetErrors();

    const newErrors = {
      artPackName: !form.art_pack_name,
      artPackNameInvalid: form.art_pack_name && !validateArtPackName(form.art_pack_name),
      copyrightNoticeUrl: !form.copyright_notice_url,
      minArtistPercentage: !form.min_artist_percentage && form.min_artist_percentage !== 0,
      minArtistPercentageRange: !validatePercentageRange(form.min_artist_percentage),
      artPackCover: !form.uploadedfile && !form.preview_thumbnail_url,
    };

    setErrors(newErrors);

    // Check if name is unique
    let isNameTaken = false;
    if (form.art_pack_name) {
      const id = typeCheck === 'updateItem' ? form.art_pack_id : 0;
      isNameTaken = await checkNameUnique(form.art_pack_name, id);
      newErrors.checkName = isNameTaken;
    }

    // Upload image if new file selected
    if (form.uploadedfile) {
      try {
        setSpinner(true);
        await artPackAPI.uploadImage(form.uploadedfile, form.preview_url);
      } catch (error) {
        setSpinner(false);
        console.error("Error uploading image:", error);
        setErrors(prev => ({ ...prev, artPackCover: "Failed to upload image" }));
        return;
      }
    }

    // Submit if validation passes
    const hasErrors = Object.values(newErrors).some(error => error === true);
    if (!hasErrors && !isNameTaken) {
      handleSubmit(form);
    }
  }, [form, typeCheck, resetErrors, checkNameUnique, handleSubmit]);

  // Determine if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    return artPack?.approved || (!hasUnsavedChanges && !artPack?.approved);
  }, [artPack?.approved, hasUnsavedChanges]);


  return (
    <>
      <div className="row">
        {/* Art Pack Name Input Field */}
        <div className="mt-4">
          <label htmlFor="art_pack_name" className="form-labels">Name</label>
          <input
            type="text"
            name="art_pack_name"
            id="art_pack_name"
            onChange={handleChange}
            value={form.art_pack_name}
            className="form-control inputs"
            maxLength={100}
          />
          {errors.artPackName && (
            <span className="form-text text-danger">
              Name is required
            </span>
          )}
          {errors.artPackNameInvalid && (
            <span className="form-text text-danger">
              Only letters, numbers, spaces, forward slash (/), hyphen (-), and underscore (_) are allowed
            </span>
          )}
          {errors.checkName && (
            <span className="form-text text-danger">
              This Art Pack Name already exists, please use a different one.
            </span>
          )}
        </div>

        {/* Copyright Notice URL Input Field */}
        <div className="mt-5">
          <label htmlFor="copyright_notice_url" className="form-labels">Copyright Notice URL</label>
          <input
            type="text"
            name="copyright_notice_url"
            id="copyright_notice_url"
            onChange={handleChange}
            value={form.copyright_notice_url}
            className="form-control inputs"
          />
          {errors.copyrightNoticeUrl && (
            <span className="form-text text-danger">
              Copyright Notice URL is required
            </span>
          )}
        </div>

        {/* Art Pack Cover Upload Section */}
        <label className="form-labels mt-5" htmlFor="ct_icon_nft" style={{ marginBottom: "0.5rem" }}>
          Art Pack Cover
        </label>
        <div
          className="mt-3 mb-2 upload-image-group"
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto 3fr",
            gridTemplateRows: "auto auto",
            alignItems: "start",
            gap: "1rem",
            width: "100%",
          }}
        >
          {/* Column 1: Upload Button */}
          <div>
            <label className="btn btn-upload form-btn">
              Upload Image
              <input
                type="file"
                name="ct_icon_nft"
                id="ct_icon_nft"
                accept="image/*"
                onChange={onImageChange}
              />
            </label>
          </div>

          {/* Column 2: OR */}
          <span style={{ fontSize: "1.5rem", alignSelf: "start" }}>or</span>

          {/* Column 3: Dropzone */}
          <div style={{ width: "100%" }}>
            <div
              {...getRootProps()}
              style={{
                border: form.preview_thumbnail_url ? "1px solid #70707099" : "1px dashed #70707099",
                padding: form.preview_thumbnail_url ? "2rem" : "4rem 2rem",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#ededed",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <input {...getInputProps()} />
              {form.preview_thumbnail_url ? (
                <img
                  src={form.preview_thumbnail_url}
                  className="custom-choose-file"
                  alt="Preview of the uploaded thumbnail"
                />
              ) : (
                <p style={{ fontSize: "1.5rem", color: "#707070" }}>
                  Drag and drop your Art Pack cover here
                </p>
              )}
            </div>
          </div>

          {/* Error message - Column 3, Row 2 */}
          <div style={{ gridColumn: "3", gridRow: "2", width: "100%", textAlign: "right" }}>
            {errors.artPackCover && (
              <span className="form-text text-danger" style={{ display: "block" }}>
                {typeof errors.artPackCover === "string" ? errors.artPackCover : "Art Pack Cover is required"}
              </span>
            )}
          </div>
        </div>

        {/* Available for Campaigns & Minimum Artist Percentage */}
        <div className="col">
          <div className="mt-4 col-sm-5">
            <div className="form-labels">Let Sponsors Know it is Available for Campaigns?</div>
            <Dropdown
              category="artist"
              categorySubType="availability"
              dropdownList={['Yes', 'No']}
              dropdownHeader={form.available_for_campaigns === true ? "Yes" : "No"}
              handleChange={handleDropdownChange}
            />
          </div>

          {/* Minimum Artist Percentage */}
          <div className="mt-4 col-sm-12">
            <div>
              <div className="form-labels">Minimum Artist Percentage</div>
              <div className="d-flex align-items-center">
                <input
                  type="number"
                  name="min_artist_percentage"
                  id="min_artist_percentage"
                  onChange={handleChange}
                  value={form.min_artist_percentage}
                  className="form-control inputs text-center"
                  max={constants.MAX_MIN_ARTIST_PERCENTAGE}
                  min={0}
                  style={{ width: '90px' }}
                />
                <span className="ms-2 fs-5">%</span>
              </div>
            </div>
            {errors.minArtistPercentage && (
              <div className="form-text text-danger">
                Minimum Artist Percentage is required
              </div>
            )}
            {errors.minArtistPercentageRange && (
              <div className="form-text text-danger">
                The percentage must be between 0% and {constants.MAX_MIN_ARTIST_PERCENTAGE}%
              </div>
            )}
            <p className="user-note" style={{ textAlign: "left", paddingLeft: 0 }}>
              Note: Fund Raisin automatically collects {apiConstants.reddevcut}% fee from the Artist.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-5">
          <div className="btn btn-secondary form-btn me-4" onClick={onDiscard}>
            Discard
          </div>
          <input
            type="submit"
            value="Save Changes"
            className="btn bg-orange form-btn"
            onClick={handleClick}
            disabled={isSaveDisabled}
          />
        </div>
      </div>
    </>
  );
};

export default Form;
